import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { clientCroppedImagesState } from '../../Recoil/Atoms';
import axios from 'axios';
import clientLogger from '../../../utils/clientLogger';
import './style.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * GalleryView component
 * 
 * This component handles the gallery URL with the session ID.
 * It fetches the cart data by session ID and redirects to the ReviewImages page.
 */
const GalleryView = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [, setClientCroppedImages] = useRecoilState(clientCroppedImagesState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [formattedImages, setFormattedImages] = useState([]);
    const [cartData, setCartData] = useState(null);
    const [isPurchased, setIsPurchased] = useState(false);
    
    // Effect to navigate as soon as cart data is ready. We deliberately do NOT
    // preload every image here — that blocked navigation until the slowest image
    // finished downloading. The grid streams images in via ImageLoader's blur-up.
    useEffect(() => {
        if (formattedImages.length > 0 && cartData) {
            clientLogger.debug('Cart data ready, navigating to review images page');
            
            // Create a map of client-side cropped images
            const clientCrops = {};
            
            // Get user info for logging
            const uid = localStorage.getItem("uniqueUserId") || "";
            const userEmail = localStorage.getItem("userEmail") || "";
            const logIdentifier = userEmail ? `[${uid}-${userEmail}]` : `[${uid}]`;
            
            // Populate clientCrops with data from the API response
            formattedImages.forEach(img => {
                if (img.clientCroppedImage) {
                    clientCrops[img._id] = img.clientCroppedImage;
                }
            });
            
            // Set the clientCroppedImagesState if we have any client crops
            if (Object.keys(clientCrops).length > 0) {
                setClientCroppedImages(clientCrops);
            }
            
            navigate('/review-your-images', { 
                state: { 
                    fromGallery: true,
                    cartData: cartData,
                    galleryImages: formattedImages
                } 
            });
        }
    }, [formattedImages, cartData, navigate, setClientCroppedImages]);

    useEffect(() => {
        const fetchCartData = async () => {
            try {
                clientLogger.debug(`Fetching cart data for sessionId: ${sessionId}`);
                
                // Add a timeout to the axios request to handle slow responses
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
                
                try {
                    // BASE_URL already includes the /api prefix, so we don't need to add it again
                    const response = await axios.get(`${BASE_URL}/addtocart/session/${sessionId}`, {
                        signal: controller.signal
                    });
                    
                    clearTimeout(timeoutId);
                    
                    if (response.data.success) {
                        // Store session data in localStorage
                        localStorage.setItem("sessionId", sessionId);
                        localStorage.setItem("uniqueUserId", response.data.cart.uid);

                        // If user info exists, store it
                        if (response.data.cart.userInfo) {
                            localStorage.setItem("fullName", response.data.cart.userInfo.fullName);
                            localStorage.setItem("userEmail", response.data.cart.userInfo.email);
                        }

                        // Log with the [uid-email] format if email is available
                        const uid = response.data.cart.uid;
                        const userEmail = response.data.cart.userInfo?.email || "";
                        const logIdentifier = userEmail ? `[${uid}-${userEmail}]` : `[${uid}]`;
                        clientLogger.debug(`${logIdentifier} Gallery accessed with sessionId: ${sessionId}`);

                        // WALL carts live in the /products wall designer, not the review
                        // flow. The session was adopted into localStorage above (the wall
                        // page reads it on mount), so continue on the product page —
                        // bundle slug is "<count>-frames", product preselected via ?p=.
                        const wallPid = response.data.cart.wallProductId || response.data.cart.wallOrder?.productId;
                        if (wallPid) {
                            const wallCount = parseInt(String(wallPid).split('-').pop(), 10);
                            clientLogger.debug(`${logIdentifier} Wall cart — continuing to product page for ${wallPid}`);
                            navigate(wallCount ? `/products/${wallCount}-frames?p=${wallPid}` : '/products', { replace: true });
                            return;
                        }

                        // Check if the order has already been purchased
                        if (response.data.cart.purchaseCompleted) {
                            clientLogger.debug(`${logIdentifier} Cart already purchased, showing notice`);
                            setIsPurchased(true);
                            setLoading(false);
                            return;
                        }

                        // Get the images from the response
                        const cartImages = response.data.cart.images || [];
                        
                        // Filter out deleted images
                        const activeImages = cartImages.filter(img => !img.isDeleted);
                        
                        // Check if there are no active images and redirect to upload page
                        if (activeImages.length === 0) {
                            clientLogger.debug(`${logIdentifier} No active images, redirecting to upload`);
                            navigate('/upload-your-image');
                            return; // Exit early to prevent further processing
                        }
                        
                        // Create image objects in the format expected by ReviewImages
                        const images = activeImages.map(img => {
                            return {
                            _id: img.imageId,
                            publicId: img.imageId,
                            crop_image: img.cropImageUrl,
                            view_image: img.viewImageUrl,
                            image: img.imageUrl,
                            cropbox_data: img.cropData,
                            bwFilter: img.bwFilter || false,
                            bluewhiteFilter: img.bluewhiteFilter || false,
                            zoomvalue: img.zoomvalue || 0,
                            rotate: img.rotate || 0,
                            imageheight: img.imageheight || 0,
                            imagewidth: img.imagewidth || 0,
                            clientCroppedImage: img.clientCroppedImage // Include client-side cropped image if available
                        };
                        });
                        
                        // Store the formatted images and cart data — the navigation
                        // effect fires as soon as these are set (no blocking preload).
                        setFormattedImages(images);
                        setCartData(response.data.cart);
                    } else {
                        console.error("Gallery API returned success: false");
                        setError("שגיאה בטעינת התמונות, נסו שוב");
                        setLoading(false);
                    }
                } catch (axiosError) {
                    clearTimeout(timeoutId);

                    if (axiosError.name === 'AbortError') {
                        console.error("Gallery request timed out");
                        setError("לא הצלחנו לטעון את התמונות, נסו שוב");
                    } else if (axiosError.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        console.error("Gallery server error:", axiosError.response.status);

                        // Check if cart is expired (HTTP 410 Gone)
                        if (axiosError.response.status === 410 && axiosError.response.data?.expired) {
                            clientLogger.debug("Cart is expired");
                            setIsExpired(true);
                            setError(axiosError.response.data.message || "אופס, נראה שהלינק ישן מדי, תעלו תמונות חדשות");
                        } else if (axiosError.response.status === 404) {
                            setError("הלינק לא נמצא, נסו שוב");
                        } else {
                            setError(`שגיאה בשרת: ${axiosError.response.status}. נסו שוב מאוחר יותר.`);
                        }
                    } else if (axiosError.request) {
                        // The request was made but no response was received
                        console.error("Gallery: No response from server");
                        setError("נסו שוב");
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        console.error("Gallery request error:", axiosError.message);
                        setError(`Error: ${axiosError.message}`);
                    }

                    setLoading(false);
                }
            } catch (error) {
                console.error("Gallery fetch error:", error.message);
                setError("שגיאה בטעינת התמונות, נסו שוב.");
                setLoading(false);
            }
        };
        
        fetchCartData();
    }, [sessionId, navigate]);
    
    if (loading) {
        return (
            <div className="gallery-loading">
                <div className="gallery-loading-spinner"></div>
                <p>התמונות מיד יופיעו</p>
            </div>
        );
    }
    
    if (isPurchased) {
        return (
            <div className="gallery-error gallery-purchased">
                <img className="purchased-icon" src="/assets/file/images/success_tick.png" width="92" height="85" alt="" aria-hidden="true" />
                <p className="purchased-message">ההזמנה בוצעה בהצלחה!</p>
                <p className="purchased-submessage">ניתן לעקוב אחרי ההזמנה באזור האישי</p>
                <button onClick={() => navigate('/personal-area')}>לאזור האישי</button>
            </div>
        );
    }

    if (error) {
        // Special UI for expired galleries
        if (isExpired) {
            return (
                <div className="gallery-error gallery-expired">
                    <div className="expired-icon">⏰</div>
                    <p className="expired-message">{error}</p>
                    <button onClick={() => navigate('/upload-your-image')}>העלאת תמונות חדשות</button>
                </div>
            );
        }

        return (
            <div className="gallery-error">
                <h2>Error</h2>
                <p>{error}</p>
                <button onClick={() => navigate('/')}>Go to Homepage</button>
            </div>
        );
    }
    
    return null; // This component should redirect, not render
};

export default GalleryView;
